import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import PhoneLink from './PhoneLink';
import ConvertGuestToTenantModal from './ConvertGuestToTenantModal';
import GuestPayModal from './GuestPayModal';
import GuestViewModal from './GuestViewModal';
import { bedService, floorService, roomService, tenantService } from '../services/api';
import { Bed, DailyGuest, Floor, Room } from '../types';
import { guestBalanceDue } from '../utils/dailyGuestDates';
import { formatLocationByIds } from '../utils/locationLabels';
import '../pages/TenantManagement.css';
import './DailyGuestsPanel.css';

function IconActPay() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="2" y="5" width="20" height="14" rx="2.5" stroke="currentColor" strokeWidth="1.75" />
      <path d="M2 10h20" stroke="currentColor" strokeWidth="1.75" />
      <path d="M6 15h4" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
    </svg>
  );
}

function IconActView() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.75" />
    </svg>
  );
}

function IconActEdit() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 20h9M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconActCheckout() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconActConvert() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M16 3h5v5M4 20 21 3M21 16v5h-5M15 15l6 6M4 4l5 5"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const ACCEPT_IMAGE_PDF = 'image/*,application/pdf,.pdf';

const emptyForm = () => ({
  name: '',
  phoneNumber: '',
  checkInDate: '',
  checkOutDate: '',
  floorId: '' as string | number,
  roomId: '' as string | number,
  bedId: '' as string | number,
  dailyRate: '',
  amountPaid: '',
  comments: '',
});

function toYmd(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatDate(v: string | undefined | null): string {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

function stayDays(checkIn: string, checkOut: string): number {
  const a = new Date(checkIn);
  const b = new Date(checkOut);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return 0;
  const ms = b.getTime() - a.getTime();
  const days = Math.ceil(ms / (1000 * 60 * 60 * 24));
  return Math.max(1, days);
}

type Props = {
  hostelId: number;
};

const DailyGuestsPanel: React.FC<Props> = ({ hostelId }) => {
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState<DailyGuest[]>([]);
  const [query, setQuery] = useState('');
  const [floors, setFloors] = useState<Floor[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [beds, setBeds] = useState<Bed[]>([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<DailyGuest | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [viewGuest, setViewGuest] = useState<DailyGuest | null>(null);
  const [payGuest, setPayGuest] = useState<DailyGuest | null>(null);
  const [convertGuest, setConvertGuest] = useState<DailyGuest | null>(null);
  const [profilePhoto, setProfilePhoto] = useState<File | null>(null);
  const [govIdFront, setGovIdFront] = useState<File | null>(null);
  const [govIdBack, setGovIdBack] = useState<File | null>(null);
  const [existingProfile, setExistingProfile] = useState<{
    photo?: string | null;
    idFrontSide?: string | null;
    idBackSide?: string | null;
  } | null>(null);

  const floorById = useMemo(() => Object.fromEntries(floors.map((f) => [f.id, f])) as Record<number, Floor>, [floors]);
  const roomById = useMemo(() => Object.fromEntries(rooms.map((r) => [r.id, r])) as Record<number, Room>, [rooms]);
  const bedById = useMemo(() => Object.fromEntries(beds.map((b) => [b.id, b])) as Record<number, Bed>, [beds]);

  const locationLabel = useCallback(
    (g: DailyGuest) => formatLocationByIds(g.floorId ?? null, g.roomId ?? null, { floorById, roomById }),
    [floorById, roomById],
  );

  const bedLabel = useCallback(
    (g: DailyGuest) => {
      const b = g.bedId != null ? bedById[Number(g.bedId)] : undefined;
      return b ? String(b.bedNumber ?? b.bedName ?? b.id) : '—';
    },
    [bedById],
  );

  const loadLookups = useCallback(async () => {
    try {
      const [fl, rm, bd] = await Promise.all([
        floorService.getFloorsByHostel(hostelId),
        roomService.getRoomsByHostel(hostelId),
        bedService.getBedsByHostel(hostelId),
      ]);
      setFloors(fl.filter((f) => f.isActive !== false));
      setRooms(rm.filter((r) => r.isActive !== false));
      setBeds(bd.filter((b) => b.isActive !== false));
    } catch {
      setFloors([]);
      setRooms([]);
      setBeds([]);
    }
  }, [hostelId]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await tenantService.getDailyGuestsByHostel(hostelId, { status: 'ACTIVE' });
      setRecords(data);
    } catch {
      toast.error('Failed to load daily guests');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [hostelId]);

  useEffect(() => {
    void loadLookups();
  }, [loadLookups]);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return records;
    return records.filter((g) => {
      const hay = [
        g.name,
        g.phoneNumber,
        g.comments,
        locationLabel(g),
        bedLabel(g),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [records, query, locationLabel, bedLabel]);

  const resetUploads = () => {
    setProfilePhoto(null);
    setGovIdFront(null);
    setGovIdBack(null);
    setExistingProfile(null);
  };

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm());
    resetUploads();
    setModalOpen(true);
  };

  const openEdit = async (g: DailyGuest) => {
    setEditing(g);
    resetUploads();
    if (g.userId != null && Number(g.userId) > 0) {
      try {
        const u = await tenantService.getHostelTenant(hostelId, Number(g.userId));
        setExistingProfile({
          photo: u.photo,
          idFrontSide: u.idFrontSide,
          idBackSide: u.idBackSide,
        });
      } catch {
        setExistingProfile(null);
      }
    }
    setForm({
      name: g.name || '',
      phoneNumber: g.phoneNumber || '',
      checkInDate: g.checkInDate ? toYmd(new Date(g.checkInDate)) : '',
      checkOutDate: g.checkOutDate ? toYmd(new Date(g.checkOutDate)) : '',
      floorId: g.floorId ?? '',
      roomId: g.roomId ?? '',
      bedId: g.bedId ?? '',
      dailyRate: g.dailyRate != null ? String(g.dailyRate) : '',
      amountPaid: g.amountPaid != null ? String(g.amountPaid) : '',
      comments: g.comments || '',
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
    setEditing(null);
    resetUploads();
  };

  const buildFormData = (): FormData => {
    const fd = new FormData();
    fd.append('name', form.name.trim());
    fd.append('phoneNumber', form.phoneNumber.trim());
    fd.append('checkInDate', form.checkInDate);
    fd.append('checkOutDate', form.checkOutDate);
    if (form.floorId) fd.append('floorId', String(form.floorId));
    if (form.roomId) fd.append('roomId', String(form.roomId));
    if (form.bedId) fd.append('bedId', String(form.bedId));
    fd.append('dailyRate', form.dailyRate ? String(form.dailyRate) : '0');
    fd.append('amountPaid', form.amountPaid ? String(form.amountPaid) : '0');
    fd.append('comments', form.comments.trim());
    if (profilePhoto) fd.append('photo', profilePhoto);
    if (govIdFront) fd.append('idFrontSide', govIdFront);
    if (govIdBack) fd.append('idBackSide', govIdBack);
    return fd;
  };

  const save = async () => {
    const name = form.name.trim();
    const phoneNumber = form.phoneNumber.trim();
    if (!name || !phoneNumber || !form.checkInDate || !form.checkOutDate) {
      toast.error('Name, phone, and stay dates are required');
      return;
    }
    if (form.checkOutDate < form.checkInDate) {
      toast.error('Check-out cannot be before check-in');
      return;
    }
    const hasFiles = Boolean(profilePhoto || govIdFront || govIdBack);
    const payload = hasFiles ? buildFormData() : {
      name,
      phoneNumber,
      checkInDate: form.checkInDate,
      checkOutDate: form.checkOutDate,
      floorId: form.floorId ? Number(form.floorId) : undefined,
      roomId: form.roomId ? Number(form.roomId) : undefined,
      bedId: form.bedId ? Number(form.bedId) : undefined,
      dailyRate: form.dailyRate ? Number(form.dailyRate) : 0,
      amountPaid: form.amountPaid ? Number(form.amountPaid) : 0,
      comments: form.comments.trim(),
    };
    try {
      setSaving(true);
      if (editing?.id) {
        await tenantService.updateDailyGuest(editing.id, payload);
        toast.success('Daily guest updated');
      } else {
        const res = await tenantService.createDailyGuest(hostelId, payload);
        const apiMsg = (res as { message?: string })?.message;
        toast.success(apiMsg || 'Daily guest added — guest can sign in with phone + default password from Settings.');
      }
      closeModal();
      await load();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Could not save daily guest');
    } finally {
      setSaving(false);
    }
  };

  const checkout = async (g: DailyGuest) => {
    const balance = guestBalanceDue(g);
    const ok = window.confirm(
      balance > 0
        ? `${g.name} has ₹${balance.toLocaleString('en-IN')} due. Check out anyway?`
        : `Check out ${g.name}?`,
    );
    if (!ok) return;
    try {
      await tenantService.updateDailyGuest(g.id, { status: 'COMPLETED' });
      toast.success('Marked as checked out');
      setViewGuest((v) => (v?.id === g.id ? null : v));
      await load();
    } catch {
      toast.error('Could not update status');
    }
  };

  const openPay = (g: DailyGuest) => {
    setViewGuest(null);
    setPayGuest(g);
  };

  const openView = (g: DailyGuest) => {
    setPayGuest(null);
    setConvertGuest(null);
    setViewGuest(g);
  };

  const openConvert = (g: DailyGuest) => {
    setViewGuest(null);
    setPayGuest(null);
    setConvertGuest(g);
  };

  const remove = async (g: DailyGuest) => {
    const ok = window.confirm(`Delete daily guest ${g.name}?`);
    if (!ok) return;
    try {
      await tenantService.deleteDailyGuest(g.id);
      toast.success('Daily guest deleted');
      await load();
    } catch {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="daily-panel">
      <div className="daily-panel-toolbar">
        <div className="daily-panel-left">
          <button type="button" className="daily-panel-add" onClick={openAdd}>
            + Add daily guest
          </button>
        </div>

        <label className="daily-panel-search">
          <span aria-hidden>🔍</span>
          <input
            type="search"
            placeholder="Search name, phone, room, bed…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
      </div>

      <section className="daily-panel-card">
        <div className="daily-panel-meta">
          <strong>{filtered.length}</strong> active guest{filtered.length === 1 ? '' : 's'}
        </div>

        <div className="daily-panel-table-wrap">
          <table className="daily-panel-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Phone</th>
                <th>Check-in</th>
                <th>Check-out</th>
                <th>Days</th>
                <th>Floor / Room</th>
                <th>Bed</th>
                <th>Rate/day</th>
                <th>Total</th>
                <th>Paid</th>
                <th>Balance</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={12} className="daily-panel-empty">
                    Loading daily guests…
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={12} className="daily-panel-empty">
                    No active daily guests.
                  </td>
                </tr>
              ) : (
                filtered.map((g) => {
                  const days = stayDays(g.checkInDate, g.checkOutDate);
                  const rate = Number(g.dailyRate) || 0;
                  const total = days * rate;
                  const paid = Number(g.amountPaid) || 0;
                  const balance = Math.max(0, total - paid);
                  return (
                    <tr key={g.id}>
                      <td className="daily-panel-name">{g.name}</td>
                      <td>
                        <PhoneLink phone={g.phoneNumber} className="daily-panel-phone" />
                      </td>
                      <td>{formatDate(g.checkInDate)}</td>
                      <td>{formatDate(g.checkOutDate)}</td>
                      <td>{days}</td>
                      <td title={locationLabel(g)}>{locationLabel(g) || '—'}</td>
                      <td>{bedLabel(g)}</td>
                      <td>₹{rate.toLocaleString('en-IN')}</td>
                      <td>₹{total.toLocaleString('en-IN')}</td>
                      <td>₹{paid.toLocaleString('en-IN')}</td>
                      <td className={balance > 0 ? 'daily-panel-balance-due' : ''}>₹{balance.toLocaleString('en-IN')}</td>
                      <td className="daily-panel-col-actions">
                        <div className="tenants-actions daily-panel-actions">
                          <button
                            type="button"
                            className="tenants-act-btn tenants-act-btn--pay"
                            title="Pay"
                            onClick={() => openPay(g)}
                          >
                            <span className="tenants-act-btn__icon" aria-hidden>
                              <IconActPay />
                            </span>
                            <span className="tenants-act-btn__label">Pay</span>
                          </button>
                          <button
                            type="button"
                            className="tenants-act-btn tenants-act-btn--view"
                            title="View"
                            onClick={() => openView(g)}
                          >
                            <span className="tenants-act-btn__icon" aria-hidden>
                              <IconActView />
                            </span>
                            <span className="tenants-act-btn__label">View</span>
                          </button>
                          <button
                            type="button"
                            className="tenants-act-btn tenants-act-btn--edit"
                            title="Edit"
                            onClick={() => openEdit(g)}
                          >
                            <span className="tenants-act-btn__icon" aria-hidden>
                              <IconActEdit />
                            </span>
                            <span className="tenants-act-btn__label">Edit</span>
                          </button>
                          {String(g.status || '').toUpperCase() === 'ACTIVE' ? (
                            <>
                              <button
                                type="button"
                                className="tenants-act-btn tenants-act-btn--convert"
                                title="Convert to tenant"
                                onClick={() => openConvert(g)}
                              >
                                <span className="tenants-act-btn__icon" aria-hidden>
                                  <IconActConvert />
                                </span>
                                <span className="tenants-act-btn__label">Tenant</span>
                              </button>
                              <button
                                type="button"
                                className="tenants-act-btn tenants-act-btn--checkout"
                                title="Check out"
                                onClick={() => void checkout(g)}
                              >
                                <span className="tenants-act-btn__icon" aria-hidden>
                                  <IconActCheckout />
                                </span>
                                <span className="tenants-act-btn__label">Out</span>
                              </button>
                            </>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>

      <GuestPayModal guest={payGuest} onClose={() => setPayGuest(null)} onSuccess={() => void load()} />

      <GuestViewModal
        guest={viewGuest}
        hostelId={hostelId}
        onClose={() => setViewGuest(null)}
        onPay={() => viewGuest && openPay(viewGuest)}
        onEdit={() => {
          if (viewGuest) {
            setViewGuest(null);
            openEdit(viewGuest);
          }
        }}
        onCheckout={() => viewGuest && void checkout(viewGuest)}
        onConvertToTenant={() => viewGuest && openConvert(viewGuest)}
      />

      <ConvertGuestToTenantModal
        guest={convertGuest}
        hostelId={hostelId}
        onClose={() => setConvertGuest(null)}
        onSuccess={() => void load()}
      />

      {modalOpen ? (
        <div className="daily-modal-backdrop" role="presentation" onClick={closeModal}>
          <div className="daily-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="daily-modal-head">
              <h3>{editing ? 'Edit daily guest' : 'Add daily guest'}</h3>
              <button type="button" className="daily-modal-close" onClick={closeModal} aria-label="Close">
                ×
              </button>
            </div>

            <div className="daily-modal-grid">
              <label>
                Name *
                <input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
              </label>
              <label>
                Phone *
                <input value={form.phoneNumber} onChange={(e) => setForm((p) => ({ ...p, phoneNumber: e.target.value }))} />
              </label>
              <label>
                Check-in *
                <input type="date" value={form.checkInDate} onChange={(e) => setForm((p) => ({ ...p, checkInDate: e.target.value }))} />
              </label>
              <label>
                Check-out *
                <input type="date" value={form.checkOutDate} onChange={(e) => setForm((p) => ({ ...p, checkOutDate: e.target.value }))} />
              </label>
              <label>
                Floor
                <select value={String(form.floorId)} onChange={(e) => setForm((p) => ({ ...p, floorId: e.target.value }))}>
                  <option value="">—</option>
                  {floors.map((f) => (
                    <option key={f.id} value={f.id}>
                      {String(f.floorNumber ?? f.id)}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Room
                <select value={String(form.roomId)} onChange={(e) => setForm((p) => ({ ...p, roomId: e.target.value }))}>
                  <option value="">—</option>
                  {rooms
                    .filter((r) => !form.floorId || Number(r.floorId) === Number(form.floorId))
                    .map((r) => (
                      <option key={r.id} value={r.id}>
                        {String(r.roomNumber ?? r.id)}
                      </option>
                    ))}
                </select>
              </label>
              <label>
                Bed
                <select value={String(form.bedId)} onChange={(e) => setForm((p) => ({ ...p, bedId: e.target.value }))}>
                  <option value="">—</option>
                  {beds
                    .filter((b) => !form.roomId || Number(b.roomId) === Number(form.roomId))
                    .map((b) => (
                      <option key={b.id} value={b.id}>
                        {String(b.bedNumber ?? b.bedName ?? b.id)}
                      </option>
                    ))}
                </select>
              </label>
              <label>
                Rate/day
                <input value={form.dailyRate} onChange={(e) => setForm((p) => ({ ...p, dailyRate: e.target.value }))} />
              </label>
              <label>
                Amount paid
                <input value={form.amountPaid} onChange={(e) => setForm((p) => ({ ...p, amountPaid: e.target.value }))} />
              </label>
              <label className="daily-modal-comments">
                Comments
                <textarea value={form.comments} onChange={(e) => setForm((p) => ({ ...p, comments: e.target.value }))} />
              </label>

              <div className="daily-modal-kyc">
                <p className="daily-modal-kyc__title">Photo &amp; ID proofs (optional)</p>
                <p className="daily-modal-kyc__hint">
                  Requires default tenant password in Settings so a guest login can store documents.
                </p>
                <label>
                  Profile photo
                  <input
                    type="file"
                    accept={ACCEPT_IMAGE_PDF}
                    onChange={(e) => setProfilePhoto(e.target.files?.[0] ?? null)}
                  />
                  {existingProfile?.photo && !profilePhoto ? (
                    <span className="daily-modal-kyc__existing">Current photo on file</span>
                  ) : null}
                </label>
                <label>
                  ID front
                  <input
                    type="file"
                    accept={ACCEPT_IMAGE_PDF}
                    onChange={(e) => setGovIdFront(e.target.files?.[0] ?? null)}
                  />
                  {existingProfile?.idFrontSide && !govIdFront ? (
                    <span className="daily-modal-kyc__existing">Current ID front on file</span>
                  ) : null}
                </label>
                <label>
                  ID back
                  <input
                    type="file"
                    accept={ACCEPT_IMAGE_PDF}
                    onChange={(e) => setGovIdBack(e.target.files?.[0] ?? null)}
                  />
                  {existingProfile?.idBackSide && !govIdBack ? (
                    <span className="daily-modal-kyc__existing">Current ID back on file</span>
                  ) : null}
                </label>
              </div>
            </div>

            <div className="daily-modal-actions">
              <button type="button" className="daily-modal-btn daily-modal-btn--ghost" onClick={closeModal} disabled={saving}>
                Cancel
              </button>
              <button type="button" className="daily-modal-btn daily-modal-btn--primary" onClick={() => void save()} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default DailyGuestsPanel;

